import { Box } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  TextField,
} from '@verantos-dev/shared-components';

import { useCopyAnalyticalDatasetDialog } from '../hooks/useCopyAnalyticalDatasetDialog';
import type { CreatedAnalyticalDataset } from '../lib/api/useCreateAnalyticalDatasetMutation';

const NAME_MAX_CHARACTERS = 60;

interface CopyAnalyticalDatasetDialogProps {
  projectId: string;
  analyticalDatasetProfileId: string;
  sourceName: string;
  onClose: () => void;
  /** Fired with the created copy before close so the caller can route into it. */
  onCopied: (profile: CreatedAnalyticalDataset) => void;
}

/**
 * Inline copy/duplicate dialog opened from an analytical-dataset row. Lives here
 * (not reused from feature-analytical-datasets) to avoid a dependency cycle.
 * Reuses the `analyticalDatasetsList.copyModal.*` message ids — registered app-wide
 * via `analyticalDatasetsMessageKeysEn`.
 */
export const CopyAnalyticalDatasetDialog = ({
  projectId,
  analyticalDatasetProfileId,
  sourceName,
  onClose,
  onCopied,
}: CopyAnalyticalDatasetDialogProps) => {
  const { formatMessage } = useIntl();
  const {
    name,
    onChangeName,
    onSubmit,
    closeDialog,
    isPending,
    submittingError,
    isDuplicateName,
    isValidToSubmit,
  } = useCopyAnalyticalDatasetDialog({
    projectId,
    analyticalDatasetProfileId,
    sourceName,
    onClose,
    onCopied,
  });

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({ id: 'analyticalDatasetsList.copyModal.cancelButton' }),
    entrySaveButtonText: formatMessage({ id: 'analyticalDatasetsList.copyModal.copyButton' }),
    submittingCancelButtonText: formatMessage({
      id: 'analyticalDatasetsList.copyModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({ id: 'analyticalDatasetsList.copyModal.submitting' }),
    successCloseButtonText: formatMessage({ id: 'analyticalDatasetsList.copyModal.closeButton' }),
    submittingErrorTitle: formatMessage({ id: 'analyticalDatasetsList.copyModal.submittingError' }),
    successTitle: formatMessage({ id: 'analyticalDatasetsList.copyModal.success.title' }),
    successText: formatMessage({ id: 'analyticalDatasetsList.copyModal.success.text' }, { name }),
  };

  return (
    <ApiWorkflowDialog
      id="copyAnalyticalDatasetDialog"
      title={formatMessage({ id: 'analyticalDatasetsList.copyModal.title' })}
      dialogMessages={dialogMessages}
      onClose={closeDialog}
      onSubmit={onSubmit}
      isValidForSubmit={isValidToSubmit}
      isSubmitting={isPending}
      submittingError={submittingError}
      hideDisplayButtonsOnSuccess
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        <TextField
          id="copy-analytical-dataset-name-input"
          displayLabel={formatMessage({ id: 'analyticalDatasetsList.copyModal.name.label' })}
          value={name}
          placeholder={formatMessage({ id: 'analyticalDatasetsList.copyModal.name.placeholder' })}
          onChange={onChangeName}
          maxCharacters={NAME_MAX_CHARACTERS}
          maxCharacterErrorMsg={formatMessage({
            id: 'analyticalDatasetsList.copyModal.maxCharacterErrorMsg',
          })}
          error={isDuplicateName}
          helperText={
            isDuplicateName
              ? formatMessage({ id: 'analyticalDatasetsList.duplicateNameError' })
              : undefined
          }
          displayNumCharacter
          width="auto"
          beepMaxCharacters
        />
      </Box>
    </ApiWorkflowDialog>
  );
};

export default CopyAnalyticalDatasetDialog;
